import { formatCurrency } from "@/lib/utils";
import { XCircle, DollarSign, Zap, Package } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface OmittedItemsCardProps {
    itemsCount: number;
    totalValue: number;
    onViewOmittedItems?: () => void;
}

export function OmittedItemsCard({ itemsCount, totalValue, onViewOmittedItems }: OmittedItemsCardProps) {
    return (
        <Card className="border border-red-500/20 dark:border-red-500/20 bg-gradient-to-br from-red-50/20 to-red-50/10 dark:from-red-950/10 dark:to-red-950/5">
            <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gradient-to-br from-red-500/80 to-rose-500/80 rounded-lg">
                        <XCircle className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                        <CardTitle className="text-base font-bold text-red-700 dark:text-red-300">Items not on insurance</CardTitle>
                        <Badge variant="outline" className="text-xs mt-1 border-red-500/20 bg-red-50/20 dark:bg-red-900/10">
                            <Package className="h-2.5 w-2.5 mr-1" />
                            Contractor Items Not in Insurance
                        </Badge>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pt-0">
                {/* Metrics Grid */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-white/70 dark:bg-gray-900/50 rounded-lg p-2.5 border border-red-200/50 dark:border-red-800/50">
                        <div className="flex items-center gap-1 mb-0.5">
                            <DollarSign className="h-3.5 w-3.5 text-red-600" />
                            <p className="text-xs text-muted-foreground">Total Value</p>
                        </div>
                        <p className="text-lg font-bold text-red-700 dark:text-red-300">{formatCurrency(totalValue)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900/50 dark:to-rose-900/50 rounded-lg p-2.5 border border-red-300/50 dark:border-red-700/50">
                        <div className="flex items-center gap-1 mb-0.5">
                            <XCircle className="h-3.5 w-3.5 text-red-600" />
                            <p className="text-xs text-muted-foreground">Items to Verify</p>
                        </div>
                        <p className="text-lg font-bold text-red-700 dark:text-red-300">{itemsCount}</p>
                    </div>
                </div>

                {/* Action buttons */}
                <Button
                    size="sm"
                    className="w-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white"
                    onClick={onViewOmittedItems}
                    data-testid="button-view-omitted"
                >
                    <Zap className="h-3.5 w-3.5 mr-1.5" />
                    Review Items
                </Button>
            </CardContent>
        </Card>
    );
}
